import { useEffect, useState } from "react";
import { GitMerge, RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PanelHeader, Callout, SubTabs } from "@/components/settings/SettingsUI";
import { useAuth } from "@/auth/AuthContext";
import { getFoundationMappingOverview, getFoundationRefStatus, syncFoundationRef, putFoundationMapping, setFoundationCustomerNumber } from "@/lib/api";
import FoundationRefPicker from "./FoundationRefPicker";

const TABS = [
  { id: "customers", label: "Customers" },
  { id: "pms", label: "Project Managers" },
  { id: "classes", label: "Project Classes" },
  { id: "tax", label: "Tax Codes" },
  { id: "vendors", label: "Vendors" },
  { id: "cost", label: "Cost Setup" },
  { id: "sync", label: "Sync Status" },
];
const STATUS_LABEL = { ok: "Synced", awaiting_access: "Awaiting Foundation access", error: "Error", never_synced: "Never synced" };
const STATUS_CLS = { ok: "bg-emerald-50 text-emerald-700", awaiting_access: "bg-amber-50 text-amber-800", error: "bg-rose-50 text-rose-700", never_synced: "bg-slate-100 text-slate-500" };

const fmt = (iso) => (iso ? String(iso).replace("T", " ").slice(0, 16) : "—");

function Table({ head, children, testId }) {
  return (
    <table className="w-full text-xs border border-slate-200 rounded-md overflow-hidden" data-testid={testId}>
      <thead className="bg-slate-50 text-slate-500 text-left"><tr>{head.map(h => <th key={h} className="px-2 py-1.5 font-medium">{h}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  );
}

export default function FoundationMappingPanel() {
  const { can } = useAuth();
  const canWrite = can("integrations.foundation_queue", "write");
  const [tab, setTab] = useState("customers");
  const [ov, setOv] = useState(null);
  const [custFilter, setCustFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [syncing, setSyncing] = useState(false);

  const load = () => getFoundationMappingOverview().then(r => setOv(r.data)).catch(() => toast.error("Could not load mapping overview"));
  useEffect(() => { load(); }, []);

  const refresh = async (listId) => {
    setSyncing(true);
    try {
      const { data } = await syncFoundationRef(listId);
      const bad = data.results.filter(r => r.status !== "ok");
      if (bad.length) toast.warning(`${bad.map(b => `${b.list_id}: ${STATUS_LABEL[b.status] || b.status}`).join(" · ")}`);
      else toast.success(`Synced ${data.results.map(r => `${r.list_id} (${r.row_count})`).join(", ")}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); } finally { setSyncing(false); }
  };

  const setMap = async (kind, key, code) => {
    try { await putFoundationMapping(kind, key, code); toast.success(code ? `Mapped → ${code}` : "Mapping cleared"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Mapping failed"); }
  };
  const setCustomer = async (clientId, code) => {
    if (!code) return;
    try {
      const { data } = await setFoundationCustomerNumber(clientId, code);
      toast.success(`Customer # ${data.customer_number} set`
        + (data.reflagged_work_orders ? ` · ${data.reflagged_work_orders} pushed job(s) re-flagged for update` : ""));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set Customer #"); }
  };

  if (!ov) return <div className="p-5 text-sm text-slate-500" data-testid="foundation-mapping-loading">Loading…</div>;
  const listStatus = Object.fromEntries((ov.lists || []).map(l => [l.list_id, l]));
  const exceptionCount = ov.customers.unmapped.length + ov.customers.mismatched.length + ov.customers.inactive.length
    + ov.project_managers.filter(p => !p.code && p.in_use).length + ov.project_classes.filter(c => !c.code).length
    + (ov.vendors?.unmapped_in_use || 0);

  return (
    <div data-testid="foundation-mapping-panel">
      <PanelHeader icon={GitMerge} title="Foundation Mapping" hint="App records ↔ Foundation codes, from synced reference data only" count={exceptionCount} countTone={exceptionCount ? "warn" : "ok"} testId="foundation-mapping-header"
        actions={(
          <div className="flex items-center gap-2 text-xs">
            <code className="px-2 py-0.5 rounded bg-slate-100 text-slate-600" data-testid="fm-target-db">{ov.required_database}</code>
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!canWrite || syncing} onClick={() => refresh()} data-testid="fm-refresh-all">
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Refresh from Foundation
            </Button>
          </div>
        )} />
      <div className="px-5 pb-5 space-y-4">
        <SubTabs items={TABS} value={tab} onChange={setTab} testId="fm-tabs" testIdPrefix="fm-tab" />

        {tab === "customers" && (
          <div className="space-y-3" data-testid="fm-customers">
            {!ov.customers.synced && <Callout tone="warn">Foundation customers list has not synced yet — Customer #s cannot be validated and <code>@customer_no</code> is withheld on every push.</Callout>}
            {[["mismatched", "New customer — # not found in Foundation", "rose"], ["inactive", "Foundation customer inactive / deleted", "amber"], ["unmapped", "New customer — no Customer # yet", "slate"]].map(([key, title, tone]) => (
              <div key={key}>
                <div className={`text-xs font-semibold mb-1 text-${tone}-700 flex items-center gap-1`}>{key !== "unmapped" && <AlertTriangle className="w-3.5 h-3.5" />}{title} ({ov.customers[key].length})</div>
                {ov.customers[key].length > 0 && (
                  <Table head={["Customer", "Current #", "Reason", "Foundation customer_no"]} testId={`fm-customers-${key}`}>
                    {ov.customers[key].map(c => (
                      <tr key={c.id} className="border-t border-slate-100" data-testid={`fm-customer-row-${c.id}`}>
                        <td className="px-2 py-1">{c.name}</td>
                        <td className="px-2 py-1 font-mono">{c.customer_number || "—"}</td>
                        <td className="px-2 py-1 text-slate-500">{c.reason || ""}</td>
                        <td className="px-2 py-1"><FoundationRefPicker listId="customers" value={null} onChange={(code) => setCustomer(c.id, code)} disabled={!canWrite || !ov.customers.synced} allowClear={false} placeholder="Pick Foundation customer…" testId={`fm-customer-pick-${c.id}`} /></td>
                      </tr>
                    ))}
                  </Table>
                )}
              </div>
            ))}
            {/* Override — admins may change a customer's Foundation # at any
                time, even when the current one matches. Re-flags that
                customer's pushed jobs so they can be re-pushed as updates. */}
            <details className="border border-slate-200 rounded-md" data-testid="fm-customers-matched-wrap">
              <summary className="px-2 py-1.5 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-50">
                Mapped &amp; matching ({(ov.customers.matched || []).length}) — override anyway
              </summary>
              <div className="p-2 space-y-2">
                <input value={custFilter} onChange={(e) => setCustFilter(e.target.value)} placeholder="Filter customers…"
                       className="h-7 px-2 text-xs border border-slate-300 rounded-md w-56" data-testid="fm-customers-filter" />
                <Table head={["Customer", "Current #", "Foundation name", "Change to"]} testId="fm-customers-matched">
                  {(ov.customers.matched || [])
                    .filter(c => !custFilter || (c.name || "").toLowerCase().includes(custFilter.toLowerCase()) || (c.customer_number || "").toLowerCase().includes(custFilter.toLowerCase()))
                    .slice(0, 50)
                    .map(c => (
                      <tr key={c.id} className="border-t border-slate-100" data-testid={`fm-customer-matched-row-${c.id}`}>
                        <td className="px-2 py-1">{c.name}</td>
                        <td className="px-2 py-1 font-mono">{c.customer_number}</td>
                        <td className="px-2 py-1 text-slate-500">{c.foundation_name}</td>
                        <td className="px-2 py-1"><FoundationRefPicker listId="customers" value={c.customer_number} onChange={(code) => setCustomer(c.id, code)} disabled={!canWrite} allowClear={false} testId={`fm-customer-override-${c.id}`} /></td>
                      </tr>
                    ))}
                </Table>
              </div>
            </details>
          </div>
        )}

        {tab === "pms" && (
          <div data-testid="fm-pms">
            <Callout>Every work order must carry a PM mapped here before it can push. Suggestions match the Foundation PM's e-mail address to the app user.</Callout>
            <Table head={["App user", "Role", "In use", "Foundation PM", "Suggestion"]} testId="fm-pm-table">
              {ov.project_managers.map(p => (
                <tr key={p.user_id} className={`border-t border-slate-100 ${!p.code && p.in_use ? "bg-amber-50/50" : ""}`} data-testid={`fm-pm-row-${p.user_id}`}>
                  <td className="px-2 py-1"><div>{p.name}</div><div className="text-slate-400">{p.email}</div></td>
                  <td className="px-2 py-1 text-slate-500">{p.role}</td>
                  <td className="px-2 py-1">{p.in_use ? "on WOs" : ""}</td>
                  <td className="px-2 py-1"><div className="flex items-center gap-2"><FoundationRefPicker listId="project_managers" value={p.code} onChange={(code) => setMap("pm", p.user_id, code)} disabled={!canWrite} testId={`fm-pm-pick-${p.user_id}`} />{p.foundation_name && <span className="text-slate-500">{p.foundation_name}{p.foundation_active === false ? " (inactive)" : ""}</span>}</div></td>
                  <td className="px-2 py-1">{p.suggested_code && (
                    <button type="button" className="text-blue-600 hover:underline" disabled={!canWrite} onClick={() => setMap("pm", p.user_id, p.suggested_code)} data-testid={`fm-pm-suggest-${p.user_id}`}>Use {p.suggested_code} · {p.suggested_name}</button>
                  )}</td>
                </tr>
              ))}
            </Table>
          </div>
        )}

        {tab === "classes" && (
          <div data-testid="fm-classes">
            <Callout>Nothing is seeded — set each app project type to its Foundation class once accounting confirms. Unmapped types push without <code>@project_class_no</code>.</Callout>
            <Table head={["App project type", "Foundation project_class_no"]} testId="fm-class-table">
              {ov.project_classes.map(c => (
                <tr key={c.project_type} className="border-t border-slate-100" data-testid={`fm-class-row-${c.project_type}`}>
                  <td className="px-2 py-1 font-mono">{c.project_type}</td>
                  <td className="px-2 py-1"><div className="flex items-center gap-2"><FoundationRefPicker listId="project_classes" value={c.code} onChange={(code) => setMap("project_class", c.project_type, code)} disabled={!canWrite} testId={`fm-class-pick-${c.project_type}`} />{c.foundation_name && <span className="text-slate-500">{c.foundation_name}</span>}</div></td>
                </tr>
              ))}
            </Table>
          </div>
        )}

        {tab === "tax" && (
          <div data-testid="fm-tax">
            <Callout>App tax jurisdiction codes (from the tax decision tree) ↔ Foundation <code>sales_taxes.tax_no</code>. Only sent when Foundation Settings → Tax parameters is not "neither".</Callout>
            <Table head={["App code", "Jurisdiction", "In use", "Foundation tax_no"]} testId="fm-tax-table">
              {ov.tax_codes.map(t => (
                <tr key={t.code} className="border-t border-slate-100" data-testid={`fm-tax-row-${t.code}`}>
                  <td className="px-2 py-1 font-mono">{t.code}</td>
                  <td className="px-2 py-1 text-slate-600">{t.description}</td>
                  <td className="px-2 py-1">{t.in_use ? "on WOs" : ""}</td>
                  <td className="px-2 py-1"><div className="flex items-center gap-2"><FoundationRefPicker listId="tax_codes" value={t.foundation_code} onChange={(code) => setMap("tax", t.code, code)} disabled={!canWrite} testId={`fm-tax-pick-${t.code}`} />{t.foundation_name && <span className="text-slate-500">{t.foundation_name}</span>}</div></td>
                </tr>
              ))}
            </Table>
          </div>
        )}

        {tab === "vendors" && (
          <div className="space-y-2" data-testid="fm-vendors">
            <Callout>ServiceOps vendor ↔ Foundation <code>vendors.vendor_no</code> for the PO push (<code>pending_po_sync_header.vendor_no</code>). An unmapped vendor withholds <strong>that</strong> PO with a reason — it never blocks other POs. Suggestion uses the vendor's own vendor # when Foundation knows it, else an exact name match.</Callout>
            {!ov.vendors?.synced && <Callout tone="warn">Foundation vendors list has not synced yet — run Refresh from Foundation (SELECT-only).</Callout>}
            <input value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)} placeholder="Filter vendors…"
                   className="h-7 px-2 text-xs border border-slate-300 rounded-md w-56" data-testid="fm-vendors-filter" />
            <Table head={["ServiceOps vendor", "Own vendor #", "On POs", "Foundation vendor_no", "Suggestion"]} testId="fm-vendor-table">
              {(ov.vendors?.rows || [])
                .filter(v => !vendorFilter || (v.name || "").toLowerCase().includes(vendorFilter.toLowerCase()))
                .map(v => (
                <tr key={v.vendor_id} className={`border-t border-slate-100 ${!v.code && v.in_use ? "bg-amber-50/50" : ""}`} data-testid={`fm-vendor-row-${v.vendor_id}`}>
                  <td className="px-2 py-1">{v.name}</td>
                  <td className="px-2 py-1 font-mono text-slate-500">{v.own_vendor_no || "—"}</td>
                  <td className="px-2 py-1">{v.in_use ? "yes" : ""}</td>
                  <td className="px-2 py-1"><div className="flex items-center gap-2"><FoundationRefPicker listId="vendors" value={v.code} onChange={(code) => setMap("vendor", v.vendor_id, code)} disabled={!canWrite || !ov.vendors?.synced} testId={`fm-vendor-pick-${v.vendor_id}`} />{v.foundation_name && <span className="text-slate-500">{v.foundation_name}{v.foundation_active === false ? " (inactive)" : ""}</span>}</div></td>
                  <td className="px-2 py-1">{v.suggested_code && (
                    <button type="button" className="text-blue-600 hover:underline" disabled={!canWrite} onClick={() => setMap("vendor", v.vendor_id, v.suggested_code)} data-testid={`fm-vendor-suggest-${v.vendor_id}`}>Use {v.suggested_code} · {v.suggested_name}</button>
                  )}</td>
                </tr>
              ))}
            </Table>
          </div>
        )}

        {tab === "cost" && (
          <div className="space-y-3" data-testid="fm-cost">
            <Callout>Exact Foundation codes the change-order and PO staging rows will carry — read from the synced <code>cost_classes</code> / <code>cost_codes</code> lists, never typed. <strong>Use tax is locked to Use Tax (10)</strong>; class 7 "Other" carries drive time / disposal only. Not configurable here. Writes stay blocked until FSI confirms the staging values.</Callout>
            {(!ov.cost_setup?.cost_classes_synced || !ov.cost_setup?.cost_codes_synced) && <Callout tone="warn">cost_classes / cost_codes have not synced yet — Refresh from Foundation.</Callout>}
            <Table head={["Class", "Purpose", "Found", "Foundation cost_class_no", "Description", "Stored as"]} testId="fm-cost-class-table">
              {(ov.cost_setup?.classes || []).map(c => (
                <tr key={c.class} className={`border-t border-slate-100 ${ov.cost_setup?.cost_classes_synced && !c.found ? "bg-rose-50/50" : ""}`} data-testid={`fm-cost-class-row-${c.class}`}>
                  <td className="px-2 py-1 font-mono">{c.class}</td>
                  <td className="px-2 py-1">{c.purpose}{c.class === ov.cost_setup?.use_tax_class && <span className="ml-1 text-[10px] font-semibold text-emerald-700" data-testid="fm-cost-use-tax-lock">locked</span>}</td>
                  <td className="px-2 py-1">{!ov.cost_setup?.cost_classes_synced ? "—" : c.found ? "yes" : <span className="text-rose-700 font-semibold">missing</span>}</td>
                  <td className="px-2 py-1 font-mono" data-testid={`fm-cost-class-code-${c.class}`}>{c.code || "—"}</td>
                  <td className="px-2 py-1 text-slate-600">{c.label || ""}{c.active === false ? " (inactive)" : ""}</td>
                  <td className="px-2 py-1 text-slate-400 font-mono">{c.stored_as || ""}</td>
                </tr>
              ))}
            </Table>
            <div className="text-xs" data-testid="fm-cost-code">
              Cost code <code className="font-mono">{ov.cost_setup?.cost_code?.wanted}</code>:{" "}
              {!ov.cost_setup?.cost_codes_synced ? <span className="text-slate-500">not checked yet (cost_codes not synced)</span>
                : ov.cost_setup?.cost_code?.found ? <span className="text-emerald-700 font-semibold">exists in Foundation — {ov.cost_setup.cost_code.label}{ov.cost_setup.cost_code.active === false ? " (inactive)" : ""}</span>
                : <span className="text-rose-700 font-semibold">NOT found in Foundation cost_codes</span>}
            </div>
            {ov.cost_setup?.all_cost_classes?.length > 0 && (
              <details className="border border-slate-200 rounded-md" data-testid="fm-cost-class-all">
                <summary className="px-2 py-1.5 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-50">All Foundation cost classes ({ov.cost_setup.all_cost_classes.length})</summary>
                <Table head={["cost_class_no", "Description", "Active"]} testId="fm-cost-class-all-table">
                  {ov.cost_setup.all_cost_classes.map(c => (
                    <tr key={c.code} className="border-t border-slate-100"><td className="px-2 py-1 font-mono">{c.code}</td><td className="px-2 py-1">{c.label}</td><td className="px-2 py-1">{c.active ? "A" : "inactive"}</td></tr>
                  ))}
                </Table>
              </details>
            )}
          </div>
        )}

        {tab === "sync" && (
          <div data-testid="fm-sync">
            <Callout>Read-only SELECTs, nightly at 02:15 (Denver) plus this button. Vanished or inactive rows are kept and flagged, never deleted.</Callout>
            <Table head={["List", "Foundation table", "Status", "Rows (active)", "Last success", "Last attempt", ""]} testId="fm-sync-table">
              {(ov.lists || []).map(l => (
                <tr key={l.list_id} className="border-t border-slate-100" data-testid={`fm-sync-row-${l.list_id}`}>
                  <td className="px-2 py-1 font-mono">{l.list_id}</td>
                  <td className="px-2 py-1 font-mono text-slate-500">{l.table}</td>
                  <td className="px-2 py-1"><span className={`px-1.5 py-0.5 rounded ${STATUS_CLS[l.status] || STATUS_CLS.error}`} data-testid={`fm-sync-status-${l.list_id}`}>{STATUS_LABEL[l.status] || l.status}</span>{l.error && <div className="text-rose-600 text-[10px] max-w-xs truncate" title={l.error}>{l.error}</div>}</td>
                  <td className="px-2 py-1">{l.row_count ?? "—"}{l.active_count != null ? ` (${l.active_count})` : ""}</td>
                  <td className="px-2 py-1 text-slate-500">{fmt(l.last_ok_at)}</td>
                  <td className="px-2 py-1 text-slate-500">{fmt(l.last_attempt_at)}</td>
                  <td className="px-2 py-1"><Button size="sm" variant="ghost" className="h-7 text-xs" disabled={!canWrite || syncing} onClick={() => refresh(l.list_id)} data-testid={`fm-sync-one-${l.list_id}`}><RefreshCw className="w-3 h-3" /></Button></td>
                </tr>
              ))}
            </Table>
            {Object.keys(listStatus).length === 0 && <div className="text-xs text-slate-400 mt-2">No sync state yet.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
